import React, { useRef, useEffect, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useParallaxTransition } from '../hooks/useParallaxTransition';

export const CinematicHeader: React.FC = () => {
  const headerRef = useRef<HTMLDivElement>(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);

  // Enhanced parallax scroll effect using custom hook
  const { header: parallaxValues } = useParallaxTransition();

  // Apple-style parallax for text elements
  const { scrollYProgress } = useScroll({
    target: headerRef,
    offset: ['start center', 'end start']
  });

  // Different parallax speeds for text layers - adjusted for proper initial positioning
  const titleY = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [60, 40, 0, -40]);
  const titleScale = useTransform(scrollYProgress, [0, 0.4, 0.8, 1], [1, 1.01, 1.02, 1.03]);
  const glowLayer1Y = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [55, 35, 5, -25]);
  const glowLayer2Y = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [65, 45, -5, -55]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!headerRef.current) return;
      
      const rect = headerRef.current.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      
      const x = (e.clientX - centerX) / (rect.width / 2);
      const y = (e.clientY - centerY) / (rect.height / 2);
      
      setMousePosition({ x: x * 0.5, y: y * 0.5 }); // Reduced sensitivity
    };

    const handleMouseEnter = () => setIsHovered(true);
    const handleMouseLeave = () => {
      setIsHovered(false);
      setMousePosition({ x: 0, y: 0 });
    };

    const header = headerRef.current;
    if (header) {
      header.addEventListener('mousemove', handleMouseMove);
      header.addEventListener('mouseenter', handleMouseEnter);
      header.addEventListener('mouseleave', handleMouseLeave);
    }

    return () => {
      if (header) {
        header.removeEventListener('mousemove', handleMouseMove);
        header.removeEventListener('mouseenter', handleMouseEnter);
        header.removeEventListener('mouseleave', handleMouseLeave);
      }
    };
  }, []);

  return (
    <motion.div 
      className="relative w-full py-24 sm:py-32 overflow-hidden overflow-x-hidden parallax-safe-scale overflow-safe"
      style={{
        y: parallaxValues.y,
        opacity: parallaxValues.opacity,
        scale: parallaxValues.scale,
        transformOrigin: 'center center',
        willChange: 'transform, opacity'
      }}
    >
      {/* Background ambient effects */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 w-[40rem] h-[20rem] -translate-x-1/2 -translate-y-1/2 rounded-full blur-[150px] opacity-30"
          style={{
            background: 'radial-gradient(circle, rgba(56, 189, 248, 0.4) 0%, transparent 70%)',
            transform: `translate(${mousePosition.x * 20}px, ${mousePosition.y * 20}px)`,
            transition: 'transform 0.3s ease-out'
          }}
        />
        <div className="absolute top-1/2 left-1/2 w-[30rem] h-[15rem] -translate-x-1/2 -translate-y-1/2 rounded-full blur-[120px] opacity-20"
          style={{
            background: 'radial-gradient(circle, rgba(168, 85, 247, 0.3) 0%, transparent 70%)',
            transform: `translate(${-mousePosition.x * 15}px, ${-mousePosition.y * 15}px)`,
            transition: 'transform 0.4s ease-out'
          }}
        />
        
        {/* Bottom gradient to blend with next section */}
        <div className="absolute bottom-0 inset-x-0 h-32 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none" />
      </div>

      {/* Main content */}
      <div className="relative z-10 text-center px-4">
        <motion.div
          ref={headerRef}
          className="inline-block relative"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ 
            duration: 1.2, 
            ease: [0.25, 0.46, 0.45, 0.94] 
          }}
        >
          {/* Glowing text layers */}
          <motion.h1 
            className="relative text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight leading-tight"
            style={{
              y: titleY,
              scale: titleScale,
              transform: `perspective(1000px) rotateY(${mousePosition.x * 3}deg) rotateX(${-mousePosition.y * 3}deg)`,
              transformStyle: 'preserve-3d',
              transition: 'transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              lineHeight: '1.1',
              paddingBottom: '0.1em'
            }}
          >
            {/* Multiple glow layers for depth */}
            <motion.span 
              className="absolute inset-0 block bg-gradient-to-r from-sky-400 via-blue-400 to-indigo-400 bg-clip-text text-transparent"
              style={{
                filter: isHovered ? 'blur(8px)' : 'blur(12px)',
                opacity: isHovered ? 0.6 : 0.4,
                y: glowLayer1Y,
                transform: `scale(1.1) translate(${mousePosition.x * 8}px, ${mousePosition.y * 8}px)`,
                zIndex: -1,
                transition: 'all 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
              }}
              animate={{
                opacity: isHovered ? [0.6, 0.8, 0.6] : [0.4, 0.5, 0.4],
              }}
              transition={{
                duration: 2.5,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              Sonic Storytelling
            </motion.span>
            
            <motion.span 
              className="absolute inset-0 block bg-gradient-to-r from-sky-400 via-blue-400 to-indigo-400 bg-clip-text text-transparent"
              style={{
                filter: isHovered ? 'blur(4px)' : 'blur(6px)',
                opacity: isHovered ? 0.4 : 0.3,
                y: glowLayer2Y,
                transform: `scale(1.05) translate(${mousePosition.x * 12}px, ${mousePosition.y * 12}px)`,
                zIndex: -1,
                transition: 'all 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
              }}
              animate={{
                opacity: isHovered ? [0.4, 0.6, 0.4] : [0.3, 0.4, 0.3],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 0.2
              }}
            >
              Sonic Storytelling
            </motion.span>

            {/* Main text */}
            <motion.span 
              className="relative block bg-gradient-to-r from-sky-400 via-blue-400 to-indigo-400 bg-clip-text text-transparent"
              animate={{
                textShadow: isHovered 
                  ? ['0 0 25px rgba(56, 189, 248, 0.9)', '0 0 35px rgba(56, 189, 248, 0.7)', '0 0 25px rgba(56, 189, 248, 0.9)']
                  : ['0 0 10px rgba(56, 189, 248, 0.4)', '0 0 15px rgba(56, 189, 248, 0.3)', '0 0 10px rgba(56, 189, 248, 0.4)'],
              }}
              transition={{
                duration: 2.5,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              style={{
                transition: 'text-shadow 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
              }}
            >
              Sonic Storytelling
            </motion.span>
          </motion.h1>
        </motion.div>
      </div>
    </motion.div>
  );
};
